import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import {
  getFiscalAgenda,
  markFiscalObligationCompleted,
  requestBoleto,
} from "@/services/fiscalService";
import { useAuth } from "@/contexts/AuthContext";
import { dashboardKeys } from "@/lib/queryKeys";
import { parseLocalDateOnly } from "@/lib/dateFormat";
import {
  flattenAgenda,
  TAX_I18N,
} from "@/pages/dashboard/dashboardAlertsModel";
import { DashboardRequiresUserAction } from "@/pages/dashboard/DashboardRequiresUserAction";
import { Proximos60Dias } from "@/pages/estado-fiscal/Proximos60Dias";
import { useUpcomingObligationsTimeline } from "@/hooks/useUpcomingObligationsTimeline";
import { MarkFiscalCompleteDialog } from "@/components/shared/MarkFiscalCompleteDialog";
import type { DashboardAlert, DashboardFinancial, DashboardMetrics } from "@fiscalo/shared-types";
import type { FiscalAgendaObligation } from "@/services/fiscalService";

function getDaysUntil(dueDate: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = parseLocalDateOnly(dueDate);
  return Math.ceil((due.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}

interface DashboardHeaderSummaryProps {
  metrics: DashboardMetrics;
  financial: DashboardFinancial;
  alerts: DashboardAlert[];
  dismissedAlertIds: string[];
  recurringDraftsReadyCount: number;
  /** When false (Gratis), hide billing amounts and fiscal agenda/health panels. */
  showPaidFiscalDashboard?: boolean;
}

export function DashboardHeaderSummary({
  metrics,
  financial,
  alerts,
  dismissedAlertIds,
  recurringDraftsReadyCount,
  showPaidFiscalDashboard = true,
}: DashboardHeaderSummaryProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAccountant = user?.role === "accountant";

  const year = new Date().getFullYear();
  const { data: agendaGrouped } = useQuery({
    queryKey: ["fiscal-agenda", year + "-01", year + "-12"],
    queryFn: () => getFiscalAgenda(year + "-01", year + "-12"),
    enabled: showPaidFiscalDashboard,
  });

  const allObligations = useMemo(
    () => (agendaGrouped ? flattenAgenda(agendaGrouped) : []),
    [agendaGrouped]
  );

  const pendingObligations = useMemo(
    () => allObligations.filter((o) => o.status !== "completed"),
    [allObligations]
  );

  const nextObligation = pendingObligations[0] ?? null;

  const monthObligations = useMemo((): FiscalAgendaObligation[] => {
    if (!nextObligation) return [];
    const targetMonth = nextObligation.dueDate.slice(0, 7);
    return pendingObligations.filter((o) => o.dueDate.slice(0, 7) === targetMonth);
  }, [pendingObligations, nextObligation]);

  const dueThisMonthCount = monthObligations.length;

  // Inline "Marcar como pago" on each timeline row — opens the same dialog flow
  // used on the estado-fiscal page.
  const [markPaidObligation, setMarkPaidObligation] =
    useState<FiscalAgendaObligation | null>(null);
  const queryClient = useQueryClient();
  const markPaidMutation = useMutation({
    mutationFn: (args: {
      obligationId: string;
      completedDate: string;
      notes?: string;
      amountPaidUyu?: number | null;
    }) =>
      markFiscalObligationCompleted(args.obligationId, {
        completedDate: args.completedDate,
        notes: args.notes,
        amountPaidUyu: args.amountPaidUyu,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      queryClient.invalidateQueries({ queryKey: ["fiscal-health"] });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() });
      toast.success(t("estadoFiscal.ivaMarkedAsPaid", "Marcado como pago"));
      setMarkPaidObligation(null);
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error", "No se pudo guardar"));
    },
  });

  const requestBoletoMutation = useMutation({
    mutationFn: requestBoleto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      toast.success(
        t(
          "fiscalAgenda.requestDocumentSuccess",
          "Document requested from accountant.",
        ),
      );
    },
    onError: () => {
      toast.error(
        t("fiscalAgenda.requestDocumentError", "Could not request document."),
      );
    },
  });

  // Mirrors fiscal-agenda's "Documento de pago" column on the Pendientes tab
  // (FiscalAgendaPaymentDocumentSection). Keep these two surfaces in sync.
  const supportsPaymentDocument = useCallback(
    (o: FiscalAgendaObligation): boolean =>
      (["IVA", "IRAE", "IRPF", "IRNR"].includes(o.taxType) &&
        o.authority === "DGI") ||
      (["BPS", "MONOTRIBUTO"].includes(o.taxType) && o.authority === "BPS"),
    [],
  );

  const isObligationInCurrentMonth = useCallback(
    (o: FiscalAgendaObligation): boolean => {
      const now = new Date();
      const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      return o.period.slice(0, 7) === currentYm;
    },
    [],
  );

  const buildTimelineActions = useCallback(
    (o: FiscalAgendaObligation) => {
      if (o.status === "completed") return undefined;

      const actions: Array<{
        label: string;
        variant: "accent" | "primary" | "ghost";
        onClick: () => void;
        disabled?: boolean;
      }> = [
        {
          label: t("dashboard.headerSummary.markAsPaid", "Marcar como pago"),
          variant: "accent",
          onClick: () => setMarkPaidObligation(o),
          disabled: markPaidMutation.isPending,
        },
      ];

      // Same decision tree as FiscalAgendaPaymentDocumentSection for the
      // tenant (non-accountant) Pendientes view.
      if (supportsPaymentDocument(o) && !isAccountant) {
        const hasBoleto = Boolean(o.boletoFileUrl && o.boletoUploadedAt);
        // For the current-period IVA DGI obligation, suppress Solicitar/Solicitado entirely
        // when the user owes nothing (saldo a favor or zero net): there's no boleto to
        // generate, so asking the accountant for one or showing it as "requested" is misleading.
        // We keep "Marcar como pago" — the user still files the return at DGI and can mark complete.
        const isCurrentPeriodIvaDgi =
          o.taxType === "IVA" && o.authority === "DGI" && isObligationInCurrentMonth(o);
        const nothingToPay = (financial.ivaAPagarNetoUyu ?? 0) <= 0;
        if (isCurrentPeriodIvaDgi && nothingToPay && !hasBoleto) {
          actions.push({
            label: t("dashboard.headerSummary.noAmountDue", "Sin saldo a pagar"),
            variant: "ghost",
            onClick: () => {},
            disabled: true,
          });
        } else if (hasBoleto) {
          actions.push({
            label: t("fiscalAgenda.downloadDocument", "Descargar"),
            variant: "ghost",
            onClick: () => window.open(o.boletoFileUrl ?? "", "_blank"),
          });
        } else if (o.boletoRequestedAt) {
          actions.push({
            label: t("fiscalAgenda.requested", "Solicitado"),
            variant: "ghost",
            onClick: () => {},
            disabled: true,
          });
        } else if (isObligationInCurrentMonth(o)) {
          actions.push({
            label: t("fiscalAgenda.requestDocument", "Solicitar documento"),
            variant: "ghost",
            onClick: () => requestBoletoMutation.mutate(o.id),
            disabled:
              requestBoletoMutation.isPending &&
              requestBoletoMutation.variables === o.id,
          });
        }
      }

      actions.push({
        label: t("dashboard.headerSummary.viewInAgenda", "Ver"),
        variant: "ghost",
        onClick: () => navigate(`/agenda?focus=${o.id}`),
      });

      return actions;
    },
    [
      financial.ivaAPagarNetoUyu,
      isAccountant,
      isObligationInCurrentMonth,
      markPaidMutation.isPending,
      navigate,
      requestBoletoMutation,
      supportsPaymentDocument,
      t,
    ],
  );

  // Shared with estado-fiscal page so both pages can't drift.
  const timelineEvents = useUpcomingObligationsTimeline({
    agendaGrouped: showPaidFiscalDashboard ? agendaGrouped : undefined,
    variant: "agenda",
    buildActions: buildTimelineActions,
  });

  // The health score card that used to live here was removed as a duplicate of
  // DashboardFiscalHealthScore (see the comment in the JSX below). Its score
  // computation, the /fiscal-health query feeding it and the three band-colour
  // classes derived from it went with it — none of them reached the DOM.

  const obligationDays = nextObligation
    ? getDaysUntil(nextObligation.dueDate)
    : null;
  const obligationUrgency: "overdue" | "dueToday" | "soon" | "upcoming" =
    !nextObligation
      ? "upcoming"
      : nextObligation.status === "overdue"
        ? "overdue"
        : obligationDays === 0
          ? "dueToday"
          : obligationDays != null && obligationDays <= 7
            ? "soon"
            : "upcoming";

  const obligationCardClass =
    obligationUrgency === "overdue"
      ? "border-[var(--fx-ink-200)] bg-[rgba(201,94,62,0.10)] shadow-sm dark:border-[rgba(201,94,62,0.30)] dark:bg-[rgba(201,94,62,0.14)]"
      : obligationUrgency === "dueToday" || obligationUrgency === "soon"
        ? "border-[var(--fx-ink-200)] bg-[rgba(232,196,144,0.18)] shadow-sm dark:border-[rgba(232,196,144,0.20)] dark:bg-[rgba(232,196,144,0.10)]"
        : "border-slate-200/60 bg-slate-50 shadow-sm dark:border-slate-800/60 dark:bg-slate-900/30";

  const obligationTextClass =
    obligationUrgency === "overdue"
      ? "text-[var(--fx-warn-text)] dark:text-[#E89978]"
      : obligationUrgency === "dueToday" || obligationUrgency === "soon"
        ? "text-[#8C5A1F] dark:text-[#E8C490]"
        : "text-slate-700 dark:text-slate-300";

  const obligationUrgencyBadgeLabel =
    nextObligation && obligationUrgency !== "upcoming"
      ? obligationDays !== null && obligationDays < 0
        ? Math.abs(obligationDays) === 1
          ? t("dashboard.headerSummary.dueOverdueDay")
          : t("dashboard.headerSummary.dueOverdueDays", {
              count: Math.abs(obligationDays),
            })
        : obligationUrgency === "dueToday"
          ? t("dashboard.headerSummary.dueToday")
          : obligationDays === 1
            ? t("dashboard.headerSummary.dueTomorrow")
            : obligationDays != null && obligationDays > 1
              ? t("dashboard.headerSummary.dueInDays", {
                  count: obligationDays,
                })
              : null
      : null;

  return (
    <>
      {/* Estado fiscal score card removed — it's already rendered above this section
          via DashboardFiscalHealthScore (.fx-score) inside the fx-grid-two on the
          dashboard, so showing it here too was a duplicate. */}

      {showPaidFiscalDashboard ? (
        <Proximos60Dias
          title={t(
            "dashboard.headerSummary.proximosVencimientos",
            "Próximos vencimientos",
          )}
          events={timelineEvents}
          ctaLabel={t("estadoFiscal.timelineCta", "Ver agenda completa")}
          onCta={() => navigate("/agenda")}
        />
      ) : (
        <DashboardRequiresUserAction
          metrics={metrics}
          alerts={alerts}
          dismissedAlertIds={dismissedAlertIds}
          recurringDraftsReadyCount={recurringDraftsReadyCount}
        />
      )}
      <MarkFiscalCompleteDialog
        obligation={markPaidObligation}
        onClose={() => setMarkPaidObligation(null)}
        defaultAmountUyu={(() => {
          if (!markPaidObligation) return undefined;
          if (markPaidObligation.taxType !== "IVA") return undefined;
          const credit = financial.ivaCreditBalanceUyu ?? 0;
          const neto = financial.ivaAPagarNetoUyu;
          const estimado = financial.ivaAPagarEstimadoUyu;
          const raw = credit > 0 && neto !== undefined ? neto : estimado;
          if (typeof raw !== "number") return undefined;
          return Math.max(0, Math.round(raw));
        })()}
        onConfirm={(completedDate, notes, amountPaidUyu) => {
          if (!markPaidObligation) return;
          markPaidMutation.mutate({
            obligationId: markPaidObligation.id,
            completedDate,
            notes: notes || undefined,
            amountPaidUyu,
          });
        }}
        isSubmitting={markPaidMutation.isPending}
      />
    </>
  );
}
